import { useContext, useMemo, useState } from "react";
import { Link_Target } from "./../../../ricos-schema/legacy/wix/rich_content/v1/index.js";
import { useBiNext } from "./../../bi-context-provider/bi-context-provider.js";
import { EditorBaseUrlContext } from "./../../editor-base-url/editor-base-url-context.js";
import { getInitialLinkData } from "./../../link/get-initial-link-data.js";
import { isPageLink } from "./../../link/is-page-link.js";
import { useEditorProps } from "./../../some-editor-props-context.js";
import { getEditableUrl } from "./../../url/get-editable-url.js";
import { getHrefUrl } from "./../../url/get-href-url.js";
import { LINK_MODAL_ID } from "../constants.js";
import { SAVE_BUTTON } from "../data-hooks/data-hooks.js";
const useLinkModalState = ({
  initialValue,
  onClose,
  onSave,
  onUnlink,
  onSaveMultiple,
  enabledLinkTypes: enabledLinkTypesList
}) => {
  const { baseUrl } = useContext(EditorBaseUrlContext);
  const logBi = useBiNext({ pluginId: "link", modalId: LINK_MODAL_ID });
  const { generalLinkSettings } = useEditorProps();
  const normalizedInitialValue = useMemo(() => {
    let link2 = initialValue || getInitialLinkData(generalLinkSettings?.initialLinkOptions);
    if (baseUrl && isPageLink(link2)) {
      link2 = { ...link2, url: getEditableUrl(link2.url, baseUrl) };
    }
    return link2;
  }, []);
  const enabledLinkTypes = useMemo(() => {
    const { enabledLinkTypes: globalEnabledLinkTypes } = generalLinkSettings || {};
    let linkTypes;
    if (enabledLinkTypesList) {
      linkTypes = enabledLinkTypesList.length > 0 ? enabledLinkTypesList : globalEnabledLinkTypes || [];
    } else {
      linkTypes = globalEnabledLinkTypes;
    }
    return linkTypes && !linkTypes.includes("web-address") ? [...linkTypes, "web-address"] : linkTypes;
  }, [enabledLinkTypesList, generalLinkSettings]);
  const [focusTarget, setFocusTarget] = useState(
    () => initialValue?.url || initialValue?.anchor ? "input" : "type-select"
  );
  const [linkType, setLinkType] = useState(
    initialValue?.anchor ? "section" : "web-address"
  );
  const [link, setLink] = useState(normalizedInitialValue);
  const [multipleSections, setMultipleSections] = useState([]);
  const [submitError, setSubmitError] = useState(null);
  const onChangeAttributes = (rel, target) => {
    setLink((data) => ({
      ...data,
      rel,
      target
    }));
  };
  const onChangeWebAddress = (url) => {
    setLink(
      (data) => data.url === url ? data : {
        ...data,
        anchor: void 0,
        url
      }
    );
    setSubmitError(null);
  };
  const onClickUnlink = () => {
    if (initialValue) {
      onUnlink(initialValue);
    }
    onClose();
  };
  const onSelectPage = (path, title) => {
    setLink((data) => ({
      ...data,
      anchor: void 0,
      defaultName: title,
      url: path
    }));
  };
  const onSelectSection = (anchor, defaultName) => {
    setLink((data) => ({
      ...data,
      anchor,
      defaultName,
      url: void 0
    }));
  };
  const onSelectType = (newLinkType) => {
    if (newLinkType !== linkType) {
      setLink(normalizedInitialValue);
      setFocusTarget("type-select");
      setSubmitError(null);
    }
    setLinkType(newLinkType);
  };
  const onSubmit = (evt) => {
    if (multipleSections.length > 0 && linkType === "section") {
      onSaveMultiple?.(multipleSections);
      onClose();
    } else if (!link.anchor && !link.url) {
      onClickUnlink();
    } else if (link.url && !getHrefUrl(link.url, baseUrl)) {
      evt?.preventDefault();
      setFocusTarget("input");
      setSubmitError("invalid-url");
    } else {
      onSave(
        link.anchor ? {
          anchor: link.anchor,
          defaultName: link.defaultName,
          target: Link_Target.SELF,
          url: void 0
        } : {
          anchor: void 0,
          defaultName: link.defaultName,
          rel: link.rel && Object.values(link.rel).filter((v) => v !== void 0).length ? link.rel : void 0,
          target: link.target,
          url: link.url
        }
      );
      onClose();
    }
    logBi({ dataHook: SAVE_BUTTON });
  };
  const onValidateWebAddress = (url) => {
    setSubmitError(url && !getHrefUrl(url, baseUrl) ? "invalid-url" : null);
  };
  return {
    focusTarget,
    link,
    linkType,
    submitError,
    multipleSections,
    enabledLinkTypes,
    onChangeAttributes,
    onChangeWebAddress,
    onClickUnlink,
    onSelectPage,
    onSelectSection,
    setMultipleSections,
    onSelectType,
    onSubmit,
    onValidateWebAddress
  };
};
export {
  useLinkModalState
};
//# sourceMappingURL=use-link-modal-state.js.map
